import React, { useCallback, useContext, useEffect, useState } from 'react';

import { differenceInCalendarMonths } from 'date-fns/differenceInCalendarMonths';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useMembershipPeriod } from '@techstyle/react-accounts';
import { useBreakpoint } from '@techstyle/react-components';
import { useIntl } from '@techstyle/react-intl';
import {
  parseDate,
  useSession,
  getDateNowFunction,
} from '@techstyle/redux-core';

import { getUserToken } from '../../actions/account';
import { trackSubmitOrderFailure } from '../../actions/checkout';
import { trackEmpInfoDrawerOpen } from '../../actions/membership';
import { mobile } from '../../styles';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import {
  isBilledEmpVip as getIsBilledEmpVip,
  isNMP,
} from '../../utils/selectors';
import useJsonCustomerDetail from '../../utils/useJsonCustomerDetail';
import { useMembership } from '../../utils/useMembership';
import { Component as EMPNewTokenInformationDrawerModal } from '../EMPNewTokenInformationDrawerModal';
import { MemberCreditsBadgeSmall } from '../MemberCreditsBadge/MemberCeditsBadge';
import { Component as MemberCreditsDrawerModal } from '../MemberCreditsDrawerModal';
import { Component as MemberCreditsDrawerModalProvider } from '../MemberCreditsDrawerModalProvider';

import { MemberCreditsNavLabelV2 } from './MemberCreditsNavLabel';

const MemberCreditsNavWrapper = styled.div`
  position: relative;
  ${props => !props.isVip && `margin-right: 4px;`}

  ${mobile`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 40px;
    line-height: 40px;
  `}
`;

const ContentWrapper = styled.section`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const MobileButtonWrapper = ({ children }) => {
  const { isMobile } = useBreakpoint();
  if (isMobile) {
    return <button type="button">{children}</button>;
  }
  return <div>{children}</div>;
};

function MemberCreditsNav({ setMemberCredits = () => {} }) {
  const { isMobile } = useBreakpoint();
  const { 'token-to-cash': tokenToCashEnabled } = useLDFlags();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { availableTokenQuantity: creditsRemaining, isVip } = useMembership();
  const getDateNow = useSelector(getDateNowFunction);
  const { sessionId } = useSession();
  const [setIsNewEmpAccount] = useState(false);

  const userIsNMP = useSelector(isNMP);
  const { isLoaded: isEmpNavSettingsLoaded, value: empNavSettings } =
    useJsonCustomerDetail('emp_nav');
  const {
    isLoaded: isEMPNewTokenModalMetadataLoaded,
    updateJsonCustomerDetail: updateEMPTokenModalJsonCustomerDetail,
    value: empNewTokenModalMetaData,
  } = useJsonCustomerDetail('viewed_emp_new_token_modal');
  const isBilledEmpVip = useSelector(getIsBilledEmpVip);
  const { previousCreditsRemaining, previousSessionId } = empNavSettings;
  const isNewSession = previousSessionId !== sessionId;
  const [isDialogVisible, setDialogVisibility] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [shouldBadgeAnimate, setShouldBadgeAnimate] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isBadgeReady, setIsBadgeReady] = useState(false);
  const {
    setIsModalOpen,
    setShouldForceNewCreditDialog,
    shouldForceNewCreditDialog,
  } = useContext(MemberCreditsDrawerModalProvider.Context);
  useMembershipPeriod();

  const handleError = useCallback(
    async err => {
      if (err && err.originalError) {
        const responseBody = await err.originalError.response.json();
        dispatch(trackSubmitOrderFailure(responseBody || {}));
      }
    },
    [dispatch]
  );

  const updateLastViewedDateAndShowDialog = useCallback(
    (date = parseDate(getDateNow())) => {
      updateEMPTokenModalJsonCustomerDetail({
        lastViewed: date,
      });
      setDialogVisibility(true);
    },
    [getDateNow, updateEMPTokenModalJsonCustomerDetail]
  );

  useEffect(() => {
    if (!isEmpNavSettingsLoaded) {
      return;
    }

    const isNewCreditValue = previousCreditsRemaining < creditsRemaining;

    // Badge should only animate if it's a new session. If the credits have
    // increased, then do not animate (and show dialog instead).
    setShouldBadgeAnimate(
      (isNewSession && !isNewCreditValue) || shouldForceNewCreditDialog
    );

    async function fetchUserTokens() {
      try {
        const {
          payload: { tokenDetails },
        } = await dispatch(getUserToken());
        if (tokenDetails && tokenDetails.length === 0) {
          setIsNewEmpAccount(true);
        }
      } catch (err) {
        handleError(err);
      }
      setIsBadgeReady(true);
    }

    if (isVip) {
      fetchUserTokens();
    } else {
      setIsBadgeReady(true);
    }
  }, [
    creditsRemaining,
    dispatch,
    handleError,
    isDialogVisible,
    isEmpNavSettingsLoaded,
    isNewSession,
    isVip,
    previousCreditsRemaining,
    sessionId,
    setIsBadgeReady,
    setIsNewEmpAccount,
    setShouldBadgeAnimate,
    shouldForceNewCreditDialog,
  ]);

  useEffect(() => {
    setMemberCredits(creditsRemaining);
  }, [creditsRemaining, setMemberCredits]);

  /**
   * We want this logic to show the dialog a maximum of 12 times per year. We accomplish this by
   * Checking the absolute value of differenceInCalendarMonths, and if the value is 1 or more it means the
   * month has changed, and we can show the dialog. The user also needs to be a billed EMP VIP and needs to
   * be in a new session.
   *
   * If the value hasn't been set in customer details yet we set a default.
   */

  useEffect(() => {
    if (
      (isEMPNewTokenModalMetadataLoaded && isBilledEmpVip && isNewSession) ||
      shouldForceNewCreditDialog
    ) {
      const { lastViewed } = empNewTokenModalMetaData;
      const currentDate = parseDate(getDateNow());

      if (typeof lastViewed === 'string') {
        if (
          Math.abs(
            differenceInCalendarMonths(currentDate, parseDate(lastViewed))
          ) >= 1
        ) {
          updateLastViewedDateAndShowDialog(currentDate);
        }
      } else {
        updateLastViewedDateAndShowDialog(currentDate);
      }
    }
  }, [
    empNewTokenModalMetaData,
    getDateNow,
    isBilledEmpVip,
    isEMPNewTokenModalMetadataLoaded,
    isNewSession,
    shouldForceNewCreditDialog,
    updateEMPTokenModalJsonCustomerDetail,
    updateLastViewedDateAndShowDialog,
  ]);

  const handleClick = () => {
    if (!isMobile) {
      return;
    }
    setIsModalOpen(true);
    dispatch(trackEmpInfoDrawerOpen());
  };

  const handleCloseDialog = e => {
    e.stopPropagation();
    setDialogVisibility(false);
    setShouldForceNewCreditDialog(false);
  };

  return (
    <MemberCreditsNavWrapper isVip={isVip}>
      <MobileButtonWrapper>
        <ContentWrapper
          onClick={handleClick}
          aria-label={
            isMobile
              ? formatMessage({
                  id: 'site_navigation.emp_credits_drawer_title',
                  defaultMessage: 'Learn More About Your Member Credits',
                })
              : undefined
          }
        >
          <MemberCreditsBadgeSmall />
          <MemberCreditsNavLabelV2 />
        </ContentWrapper>
      </MobileButtonWrapper>
      {userIsNMP && tokenToCashEnabled ? null : (
        <EMPNewTokenInformationDrawerModal
          isOpen={!!isDialogVisible}
          onExit={handleCloseDialog}
        />
      )}

      {isMobile ? <MemberCreditsDrawerModal /> : null}
    </MemberCreditsNavWrapper>
  );
}

MemberCreditsNav.propTypes = {
  setMemberCredits: PropTypes.func,
};

export default MemberCreditsNav;
